import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { CheckCircle2, ExternalLink, Fingerprint, ListRestart, Loader2, RefreshCcw, RefreshCw, Upload } from "lucide-react";
import { toast } from "sonner";
import { sumsubHub } from "@/lib/api";
import { AnswerPill, LabelPill, StatusPill, fmtTs, nice } from "./sumsubShared";
import { QuickConfirm, runAction } from "./SumsubActions";
import { SumsubAddDocDialog } from "./SumsubAddDocDialog";
import { SumsubDetail } from "./SumsubDetail";

const BTN = "h-6 px-1.5 text-[11px]";

function FailedStep({ s, applicantId, canAct, onDone, onAddDoc }) {
  const [busy, setBusy] = useState(false);
  const reset = async () => {
    setBusy(true);
    try { const r = await runAction(applicantId, "reset_step", { step: s.key }); toast.success(`${s.label} reset — the applicant re-does it on the same link`); onDone(r); }
    catch (e) { toast.error(e.response?.data?.detail || e.message, { duration: 8000 }); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded border border-red-200 bg-red-50/60 p-2" data-testid={`digid-failed-${s.key}`}>
      <div className="flex flex-wrap items-center gap-1.5 text-xs">
        <span className="font-medium text-red-900">{s.label}</span>
        {s.doc_type && <span className="text-red-800/70">· {nice(s.doc_type)}{s.country ? ` (${s.country})` : ""}</span>}
        {s.reject_labels?.map((l) => <LabelPill key={l} tone="red">{nice(l)}</LabelPill>)}
        {s.reject_type && <LabelPill tone="amber">{s.reject_type}</LabelPill>}
        <span className="ml-auto flex items-center gap-1">
          {s.doc_types?.length > 0 && (s.key === "IDENTITY" || s.key === "PROOF_OF_RESIDENCE") && (
            <Button size="sm" variant="outline" className={`${BTN} border-sky-300 bg-white text-sky-800 hover:bg-sky-50`} disabled={!canAct} onClick={() => onAddDoc(s.key)} data-testid={`digid-adddoc-${s.key}`}>
              <Upload className="w-3 h-3 mr-1" />Add document
            </Button>
          )}
          <Button size="sm" variant="ghost" className={`${BTN} text-amber-800 hover:bg-amber-100`} disabled={!canAct || busy} onClick={reset} title="Clear only this step — the applicant re-does it on the same link" data-testid={`digid-reset-${s.key}`}>
            {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <ListRestart className="w-3 h-3 mr-1" />}Reset step
          </Button>
        </span>
      </div>
      {s.moderation_comment && <p className="mt-1 text-[11px] text-red-900/80" data-testid={`digid-reason-${s.key}`}>“{s.moderation_comment}”</p>}
    </div>
  );
}

function ApplicantRow({ a, deal, canAct, onChanged }) {
  const [addDoc, setAddDoc] = useState(null);
  const [open, setOpen] = useState(false);
  const failed = a.failed_steps || [];
  const retryable = a.review_answer === "RED" && a.reject_type !== "FINAL";
  const files_from = a.linked?.item_id ? a.linked : deal?.item_id ? deal : null;
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-2.5 shadow-sm" data-testid={`digid-applicant-${a.applicant_id}`}>
      <div className="flex flex-wrap items-center gap-1.5">
        <button type="button" className="text-sm font-medium text-slate-900 hover:underline" onClick={() => setOpen(true)} data-testid={`digid-open-${a.applicant_id}`}>{a.full_name || "Unnamed applicant"}</button>
        <StatusPill status={a.review_status} testId={`digid-status-${a.applicant_id}`} /><AnswerPill answer={a.review_answer} testId={`digid-answer-${a.applicant_id}`} />
        {a.reject_type && <LabelPill tone={a.reject_type === "FINAL" ? "red" : "amber"}>{a.reject_type}</LabelPill>}
        <span className="text-[10px] text-slate-500">{a.workspace === "AUS" ? "Australia" : "UK"}{a.review_date ? ` · ${fmtTs(a.review_date)}` : ""}{a.linked?.sf ? "" : a.match ? ` · looks like this deal (${a.match.score}%)` : ""}</span>
        <span className="ml-auto flex items-center gap-1">
          <Button size="sm" variant="ghost" className={BTN} onClick={() => setOpen(true)} data-testid={`digid-hub-${a.applicant_id}`}><Fingerprint className="w-3 h-3 mr-1 text-emerald-600" />Full check</Button>
          <a href={a.cockpit_url} target="_blank" rel="noreferrer" data-testid={`digid-cockpit-${a.applicant_id}`}><Button size="sm" variant="ghost" className={BTN}><ExternalLink className="w-3 h-3" /></Button></a>
        </span>
      </div>
      {failed.length > 0 ? (
        <div className="mt-2 space-y-1.5" data-testid={`digid-failed-list-${a.applicant_id}`}>
          {failed.map((s) => <FailedStep key={s.key} s={s} applicantId={a.applicant_id} canAct={canAct} onDone={onChanged} onAddDoc={setAddDoc} />)}
        </div>
      ) : a.review_answer === "RED" && a.reject_labels?.length > 0 ? (
        <div className="mt-2 flex flex-wrap gap-1" data-testid={`digid-labels-${a.applicant_id}`}>{a.reject_labels.map((l) => <LabelPill key={l} tone="red">{nice(l)}</LabelPill>)}</div>
      ) : null}
      <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid={`digid-actions-${a.applicant_id}`}>
        <Button size="sm" variant="outline" className={`${BTN} border-sky-300 text-sky-800 hover:bg-sky-50`} disabled={!canAct} onClick={() => setAddDoc("PROOF_OF_RESIDENCE")} title={canAct ? "Add a file from Monday or this computer, then re-check" : "Underwriting permission needed"} data-testid={`digid-adddoc-${a.applicant_id}`}>
          <Upload className="w-3 h-3 mr-1" />Add document
        </Button>
        <QuickConfirm kind="request_recheck" applicantId={a.applicant_id} canAct={canAct} onDone={onChanged}>
          <Button size="sm" variant="outline" className={BTN} disabled={!canAct} data-testid={`digid-recheck-${a.applicant_id}`}><RefreshCcw className="w-3 h-3 mr-1 text-sky-600" />Re-check</Button>
        </QuickConfirm>
        <QuickConfirm kind="approve" applicantId={a.applicant_id} canAct={canAct} onDone={onChanged}>
          <Button size="sm" variant="outline" className={`${BTN} border-emerald-300 text-emerald-800 hover:bg-emerald-50`} disabled={!canAct} data-testid={`digid-approve-${a.applicant_id}`}><CheckCircle2 className="w-3 h-3 mr-1" />Approve</Button>
        </QuickConfirm>
        {retryable && <span className="text-[10px] text-amber-800">Failed attempt — the customer can retry on the same link, or fix it here.</span>}
      </div>
      {addDoc && (
        <SumsubAddDocDialog applicant={{ id: a.applicant_id, workspace: a.workspace, country: a.country, full_name: a.full_name }} steps={a.steps || []} linked={files_from} preset={addDoc}
          onClose={() => setAddDoc(null)} onDone={onChanged} />
      )}
      {open && <SumsubDetail applicantId={a.applicant_id} onClose={() => setOpen(false)} onChanged={onChanged} />}
    </div>
  );
}

// "Digital ID" panel on a deal (Mirror drawer → Docs): the Sumsub applicant(s) for this SF with the failed step + reason and the fixes
// (Add document · Re-check · Reset step · Approve) right there — no trip to the Sumsub tab. UK and AU alike.
export function SumsubDigitalIdPanel({ sf, itemId, region }) {
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const load = useCallback(async (quiet = false) => {
    if (!quiet) setState((s) => ({ ...s, loading: true }));
    try { setState({ loading: false, data: await sumsubHub.forDeal({ sf, itemId, region }), error: null }); }
    catch (e) { setState({ loading: false, data: null, error: e.response?.data?.detail || e.message }); }
  }, [sf, itemId, region]);
  useEffect(() => { if (sf || itemId) load(); }, [load, sf, itemId]);
  const apps = state.data?.applicants || [];
  const bad = apps.filter((a) => a.review_answer === "RED").length;
  return (
    <section className="mb-3" data-testid="digid-panel">
      <h3 className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
        <Fingerprint className="w-3 h-3" />Digital ID · Sumsub
        {state.loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <span className="normal-case tracking-normal">({apps.length}{bad ? ` · ${bad} failed` : ""})</span>}
        <button type="button" className="ml-auto text-slate-400 hover:text-slate-700" onClick={() => load()} title="Refresh" data-testid="digid-refresh"><RefreshCw className="w-3 h-3" /></button>
      </h3>
      {state.error && <div className="text-xs text-rose-700" data-testid="digid-error">{state.error}</div>}
      {!state.loading && !state.error && apps.length === 0 && (
        <div className="rounded border border-dashed border-slate-200 px-2.5 py-2 text-[11px] text-slate-500" data-testid="digid-empty">No Sumsub check is attached to this deal yet. Attach one from the Sumsub tab (it suggests matches by name and email).</div>
      )}
      {apps.length > 0 && <div className="space-y-2">{apps.map((a) => <ApplicantRow key={a.applicant_id} a={a} deal={{ item_id: itemId, sf }} canAct={!!state.data?.can_act} onChanged={() => load(true)} />)}</div>}
    </section>
  );
}
